/**
 * API client for browser → Hono API communication.
 *
 * All requests go through Next.js rewrites (/api/* → internal API).
 * Two clients:
 * - `api` — Hono RPC typed client (preferred for all JSON endpoints)
 * - `apiRawFetch` — raw fetch for SSE streams
 *
 * Auth is handled via a module-level token getter set once in Providers.
 */

import type { AppType } from "@atlas/api";
import { hc } from "hono/client";

const API_BASE = "/api";

// ── Token getter (set once in Providers) ────────────────────

let _getToken: (() => Promise<string | null>) | null = null;

export function setTokenGetter(fn: () => Promise<string | null>) {
  _getToken = fn;
}

// ── Org override for admin impersonation ────────────────────

const ORG_OVERRIDE_KEY = "atlas:admin:orgOverride";

let _orgOverride: string | null =
  typeof sessionStorage !== "undefined"
    ? sessionStorage.getItem(ORG_OVERRIDE_KEY)
    : null;

const orgOverrideListeners = new Set<() => void>();
export function subscribeOrgOverride(listener: () => void) {
  orgOverrideListeners.add(listener);
  return () => {
    orgOverrideListeners.delete(listener);
  };
}

export function setOrgOverride(orgId: string | null) {
  _orgOverride = orgId;
  if (typeof sessionStorage !== "undefined") {
    if (orgId) {
      sessionStorage.setItem(ORG_OVERRIDE_KEY, orgId);
    } else {
      sessionStorage.removeItem(ORG_OVERRIDE_KEY);
    }
  }
  for (const listener of orgOverrideListeners) listener();
}

export function getOrgOverride(): string | null {
  return _orgOverride;
}

// ── Auth headers ────────────────────────────────────────────

async function authHeaders(): Promise<Record<string, string>> {
  const h: Record<string, string> = {};
  const token = await _getToken?.();
  if (token) h.Authorization = `Bearer ${token}`;
  if (_orgOverride) h["x-org-id"] = _orgOverride;
  return h;
}

// ── Raw fetch (for SSE streams) ─────────────────────────────

export async function apiRawFetch(
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  const auth = await authHeaders();
  for (const [k, v] of Object.entries(auth)) headers.set(k, v);
  return fetch(`${API_BASE}${path}`, { ...init, headers });
}

// ── Hono RPC typed client ───────────────────────────────────
export const api = hc<AppType>(API_BASE, {
  headers: authHeaders,
});
